import React from 'react'
import PropTypes from 'prop-types'
import Popover from '@mui/material/Popover'
import CheckIcon from '@mui/icons-material/Check'
import CloseIcon from '@mui/icons-material/Close'
import './confirmation-popover.scss'

/**
 * Common reusable confirmation popover component.
 * Renders an anchor-positioned popover with "Are you sure?" title and Yes/No action buttons.
 *
 * @param {Object} props
 * @param {boolean} props.open - Indicates whether the popover is visible
 * @param {HTMLElement|null} props.anchorEl - DOM element to which the popover anchors
 * @param {Function} props.onClose - Handler invoked when user cancels or clicks outside
 * @param {Function} props.onConfirm - Handler invoked when user clicks the confirm button
 * @param {string} [props.title='Are you sure?'] - Confirmation question or message
 * @param {string} [props.confirmText='Yes'] - Label for confirmation button
 * @param {string} [props.cancelText='No'] - Label for cancel button
 * @param {string} [props.confirmVariant='primary'] - Button style variant ('primary' | 'danger')
 * @param {'center'|'left'|'right'|'none'} [props.arrowPosition='center'] - Alignment for bottom triangle arrow
 * @param {Object} [props.anchorOrigin] - Origin point on anchor element
 * @param {Object} [props.transformOrigin] - Origin point on popover container
 * @param {string} [props.className] - Additional CSS class name for popover paper
 * @param {string} [props.id] - Popover element DOM ID
 * @returns {React.ReactElement}
 */
export default function ConfirmationPopover({
  open,
  anchorEl,
  onClose,
  onConfirm,
  title = 'Are you sure?',
  confirmText = 'Yes',
  cancelText = 'No',
  confirmVariant = 'primary',
  arrowPosition = 'center',
  anchorOrigin = { vertical: 'top', horizontal: 'center' },
  transformOrigin = { vertical: 'bottom', horizontal: 'center' },
  className = '',
  id = 'confirmation-popover',
}) {
  const arrowClass = arrowPosition && arrowPosition !== 'none' ? `arrow-${arrowPosition}` : ''

  return (
    <Popover
      id={id}
      open={open && Boolean(anchorEl)}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={anchorOrigin}
      transformOrigin={transformOrigin}
      classes={{ paper: `confirmation-popover shadow border ${arrowClass} ${className}`.trim() }}
      disableScrollLock
    >
      <div className="confirmation-popover-content text-center">
        <div className="confirmation-popover-title">{title}</div>
        <div className="d-flex justify-content-center gap-2">
          <button
            type="button"
            className={`btn btn-${confirmVariant} btn-sm d-inline-flex align-items-center px-2 py-1`}
            onClick={onConfirm}
          >
            <CheckIcon sx={{ fontSize: 14, mr: 0.5 }} /> {confirmText}
          </button>
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm d-inline-flex align-items-center px-2 py-1"
            onClick={onClose}
          >
            <CloseIcon sx={{ fontSize: 14, mr: 0.5 }} /> {cancelText}
          </button>
        </div>
      </div>
    </Popover>
  )
}

ConfirmationPopover.propTypes = {
  open: PropTypes.bool.isRequired,
  anchorEl: PropTypes.any,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  title: PropTypes.string,
  confirmText: PropTypes.string,
  cancelText: PropTypes.string,
  confirmVariant: PropTypes.oneOf(['primary', 'danger', 'success', 'warning']),
  arrowPosition: PropTypes.oneOf(['center', 'left', 'right', 'none']),
  anchorOrigin: PropTypes.shape({
    vertical: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    horizontal: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
  transformOrigin: PropTypes.shape({
    vertical: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    horizontal: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
  className: PropTypes.string,
  id: PropTypes.string,
}
